export function TaxAdvantagesInstallment() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block bg-teal-100 text-teal-800 px-6 py-2 rounded-full font-bold text-sm mb-4">
            BENEFIT #4
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Significant Tax Advantages Through Installment Sales
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Defer capital gains taxes and spread your tax liability over years or decades instead of paying everything at once
          </p>
        </div>

        <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-md mb-12 max-w-4xl mx-auto">
          <div className="flex items-start">
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
              <i className="ri-alert-line text-3xl text-amber-600"></i>
            </div>
            <div className="ml-4">
              <h4 className="text-xl font-bold text-gray-900 mb-2">Important Tax Disclosure</h4>
              <p className="text-gray-700 leading-relaxed">
                <strong>Every tax situation is unique and different.</strong> The tax benefits described below may not apply to your specific circumstances. Tax laws vary by state, property type, holding period, depreciation recapture, and individual financial situation. You must consult with a qualified tax advisor, CPA, or tax attorney to determine if these strategies apply to your situation before making any decisions. This information is for educational purposes only and is not tax advice.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/c0548dee7f5dd9dff7a7e256eec557ee.jpg"
              alt="Tax advantages and capital gains deferral through installment sales"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>

          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Defer Capital Gains Taxes with Installment Sales
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              One of the most powerful tax benefits of seller financing is the ability to use <strong>installment sale treatment</strong> under IRS Section 453. Instead of recognizing all your capital gains in the year of sale and paying a massive tax bill all at once, you can spread the gain recognition—and the tax liability—over the life of the loan.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              With installment sale treatment, you only pay capital gains taxes on the portion of principal you receive each year. This means if you receive payments over 15 years, you spread your tax liability over 15 years instead of paying everything in year one. This can result in <strong>substantial tax savings</strong> by keeping you in lower tax brackets and allowing you to manage your taxable income strategically.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              According to Investopedia and Hall CPA, installment sales can significantly reduce your overall tax burden, especially for high-value properties with substantial capital gains. The ability to defer taxes also means you keep more capital working for you through the interest income you're earning on the financed amount.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Tax Payment Comparison: Lump Sum vs. Installment Sale
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-alarm-warning-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900">Traditional Sale</h4>
              </div>
              <p className="text-gray-700 mb-4">All taxes due in year of sale</p>
              <div className="space-y-3">
                <div className="flex justify-between text-lg">
                  <span>Sale Price:</span>
                  <span className="font-bold">$2,000,000</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span>Original Basis:</span>
                  <span className="font-bold">-$800,000</span>
                </div>
                <div className="flex justify-between text-lg border-t border-red-300 pt-2">
                  <span>Capital Gain:</span>
                  <span className="font-bold">$1,200,000</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span>Tax Rate (20%):</span>
                  <span className="font-bold">20%</span>
                </div>
                <div className="flex justify-between text-2xl font-bold text-red-600 border-t-2 border-red-400 pt-3">
                  <span>Year 1 Tax:</span>
                  <span>$240,000</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                Plus potential state taxes and depreciation recapture
              </p>
            </div>

            <div className="bg-teal-50 border-2 border-teal-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-calendar-check-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900">Installment Sale</h4>
              </div>
              <p className="text-gray-700 mb-4">Taxes spread over 15 years</p>
              <div className="space-y-3">
                <div className="flex justify-between text-lg">
                  <span>Sale Price:</span>
                  <span className="font-bold">$2,000,000</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span>Down Payment (20%):</span>
                  <span className="font-bold">$400,000</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span>Financed Amount:</span>
                  <span className="font-bold">$1,600,000</span>
                </div>
                <div className="flex justify-between text-lg border-t border-teal-300 pt-2">
                  <span>Year 1 Principal:</span>
                  <span className="font-bold">~$106,000</span>
                </div>
                <div className="flex justify-between text-2xl font-bold text-teal-600 border-t-2 border-teal-400 pt-3">
                  <span>Year 1 Tax:</span>
                  <span>~$63,600</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                Remaining taxes spread over 14 more years
              </p>
            </div>
          </div>

          <div className="text-center bg-teal-600 text-white rounded-lg p-6">
            <div className="text-4xl font-bold mb-2">$176,400</div>
            <div className="text-xl">Tax Savings in Year 1 Alone</div>
            <p className="text-teal-100 mt-2">
              Keep more capital working for you instead of paying it all to the IRS
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          <div className="bg-white rounded-lg shadow-md p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Additional Tax Benefits
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                <div>
                  <strong className="text-gray-900">Lower Tax Brackets:</strong>
                  <p className="text-gray-700">Spreading income over multiple years can keep you in lower tax brackets each year</p>
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                <div>
                  <strong className="text-gray-900">Interest Income Deduction:</strong>
                  <p className="text-gray-700">Interest you pay on any remaining mortgage may be deductible against interest income received</p>
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                <div>
                  <strong className="text-gray-900">Estate Planning Benefits:</strong>
                  <p className="text-gray-700">Installment notes can be valuable estate planning tools for transferring wealth</p>
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                <div>
                  <strong className="text-gray-900">Depreciation Recapture Deferral:</strong>
                  <p className="text-gray-700">Spread depreciation recapture taxes over the installment period</p>
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-2xl text-teal-600 mr-3 flex-shrink-0"></i>
                <div>
                  <strong className="text-gray-900">Strategic Tax Planning:</strong>
                  <p className="text-gray-700">Coordinate installment income with other deductions and losses for optimal tax efficiency</p>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <img
              src="https://readdy.ai/api/search-image?query=tax%20planning%20strategy%20meeting%20with%20financial%20advisor%20and%20client%20reviewing%20installment%20sale%20benefits%20charts%20showing%20tax%20savings%20over%20time%20professional%20office%20setting%20clean%20bright%20background%20business%20consultation&width=800&height=600&seq=tax-planning-consultation&orientation=landscape"
              alt="Tax planning consultation for seller financing installment sales"
              className="w-full h-full object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 md:p-12 text-white">
          <h3 className="text-3xl font-bold mb-6 text-center">Key Tax Considerations</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-3-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold mb-3 text-center">IRS Section 453</h4>
              <p className="text-gray-300 text-center leading-relaxed">
                Governs installment sale treatment and allows deferral of capital gains recognition
              </p>
            </div>

            <div>
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calculator-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold mb-3 text-center">Gross Profit Ratio</h4>
              <p className="text-gray-300 text-center leading-relaxed">
                Determines what percentage of each payment is taxable gain vs. return of basis
              </p>
            </div>

            <div>
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-star-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold mb-3 text-center">Professional Guidance</h4>
              <p className="text-gray-300 text-center leading-relaxed">
                Always work with qualified tax professionals to structure installment sales properly
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}